import { Card } from "@/components/ui/card";
import { useTireEvents } from "@/hooks/use-tire-events";
import { CircleDashed, Package, Wrench, ArrowRightLeft, Trash } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface TireEventTimelineProps {
  tireId: string;
}

const eventIcons = {
  installation: CircleDashed,
  removal: Package,
  repair: Wrench,
  rotation: ArrowRightLeft,
  discard: Trash
};

const eventLabels = {
  installation: 'Instalação',
  removal: 'Remoção',
  repair: 'Reparo',
  rotation: 'Rodízio',
  discard: 'Descarte'
};

export function TireEventTimeline({ tireId }: TireEventTimelineProps) {
  const { data: events = [], isLoading } = useTireEvents(tireId);

  if (isLoading) {
    return <div className="text-center p-4">Carregando histórico...</div>;
  }

  if (events.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        Nenhum evento registrado para este pneu.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {events.map((event, index) => {
        const Icon = eventIcons[event.event_type as keyof typeof eventIcons] || CircleDashed;
        const label = eventLabels[event.event_type as keyof typeof eventLabels] || event.event_type;

        return (
          <Card key={event.id} className="p-4">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="rounded-full bg-primary/10 p-2">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                {index < events.length - 1 && (
                  <div className="w-px bg-border flex-1 my-2" />
                )}
              </div>

              <div className="flex-1 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold">{label}</h4>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(event.occurred_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  {(event.parts_cost || event.labor_cost) && (
                    <span className="text-sm font-medium">
                      R$ {((event.parts_cost || 0) + (event.labor_cost || 0)).toFixed(2)}
                    </span>
                  )}
                </div>

                {event.vehicle && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Veículo:</span>{' '}
                    {event.vehicle.plate} - {event.vehicle.make} {event.vehicle.model}
                  </p>
                )}

                {event.from_position && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">De:</span> {event.from_position}
                  </p>
                )}

                {event.to_position && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Para:</span> {event.to_position}
                  </p>
                )}

                {event.reason && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Motivo:</span> {event.reason}
                  </p>
                )}

                {event.description && (
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
